import { useState, useEffect, useId } from 'react'
import {
  fromStoredGovernance,
  toStoredGovernance,
  PER_CUSTOMER_PERIODS,
  type PerCustomerPeriod,
} from '@marva/shared/discount-governance-form'
import { useWorkspaceStore, type Workspace } from '../../stores/workspaceStore'
import { workspacesApi, governanceApi } from '../../services/api'
import { API_BASE_URL } from '../../config/env'
import { matchesStored } from './storedValue'
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Eyebrow,
  Field,
  FieldGrid,
  Hint,
  Meter,
  SaveBar,
  SettingRow,
  Slider,
  Stack,
  TileGrid,
  Toggle,
} from '../../ds'

interface DiscountGovernanceSectionProps {
  onClose: () => void
}

interface ChannelLimits {
  chat: number
  cart_recovery: number
  post_browse: number
  inspiration: number
  email_reply: number
  sms: number
}

interface CustomerTypeLimits {
  new: number
  returning: number
  vip: number
}

interface EscalationRules {
  thresholdPercent: number
  thresholdCartValue: number
}

interface BudgetConfig {
  period: 'daily' | 'weekly' | 'monthly'
  amount: number
}

/** Stored as `customerLimits` — the key `checkCustomerLimit` reads (MAR-311). */
interface CustomerLimits {
  maxPerCustomer: number
  period: PerCustomerPeriod
}

/**
 * Stored as `productExclusions` (MAR-311). Product ids only: no cart item reaching the
 * enforcer carries a category or tags, so those fields could never match and are gone
 * until server-side enrichment brings them back.
 */
interface ProductExclusions {
  productIds: string[]
}

interface FormData {
  enabled: boolean
  maxDiscountPercent: number
  minCartValue: number
  codeExpiryHours: number
  createInShopify: boolean
  freeShippingEnabled: boolean
  freeShippingMinOrder: number | ''
  channelLimits: ChannelLimits
  customerTypeLimits: CustomerTypeLimits
  budget: BudgetConfig
  customerLimits: CustomerLimits
  escalation: EscalationRules
  productExclusions: ProductExclusions
}

type StoredDiscountGovernance = NonNullable<Workspace['discountGovernance']>

const PER_CUSTOMER_PERIOD_LABELS: Record<PerCustomerPeriod, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
}

const DEFAULT_FORM: Omit<FormData, 'customerLimits' | 'productExclusions'> = {
  enabled: false,
  maxDiscountPercent: 15,
  minCartValue: 0,
  codeExpiryHours: 24,
  createInShopify: true,
  freeShippingEnabled: false,
  freeShippingMinOrder: '',
  channelLimits: {
    chat: 15,
    cart_recovery: 20,
    post_browse: 10,
    inspiration: 15,
    email_reply: 15,
    sms: 10,
  },
  customerTypeLimits: {
    new: 15,
    returning: 12,
    vip: 20,
  },
  budget: {
    period: 'monthly',
    amount: 10000,
  },
  escalation: {
    thresholdPercent: 25,
    thresholdCartValue: 500, // major currency units ($500) — matches discount-governance Lambda
  },
}

/**
 * Stored block → form state. The two safeguards come from `fromStoredGovernance`, the ONE
 * reader shared with the migration and the enforcer's keys; everything else keeps the
 * defaults this form has always shown where the stored row is silent.
 */
const formFromStored = (stored: StoredDiscountGovernance | undefined): FormData => {
  const dg = fromStoredGovernance(stored)
  const s = (stored ?? {}) as Partial<StoredDiscountGovernance>
  return {
    enabled: s.enabled || false,
    maxDiscountPercent: s.maxDiscountPercent || DEFAULT_FORM.maxDiscountPercent,
    minCartValue: s.minCartValue || 0,
    codeExpiryHours: s.codeExpiryHours || DEFAULT_FORM.codeExpiryHours,
    createInShopify: s.createInShopify !== false,
    freeShippingEnabled: s.freeShippingEnabled === true,
    freeShippingMinOrder:
      typeof s.freeShippingMinOrder === 'number' && s.freeShippingMinOrder > 0 ? s.freeShippingMinOrder : '',
    channelLimits: { ...DEFAULT_FORM.channelLimits, ...s.channelLimits } as ChannelLimits,
    customerTypeLimits: { ...DEFAULT_FORM.customerTypeLimits, ...s.customerTypeLimits } as CustomerTypeLimits,
    // `dg.budget` is the stored budget with the legacy per-customer keys removed; any other
    // key on it (one this form does not edit) rides along and survives the save.
    budget: { ...DEFAULT_FORM.budget, ...(dg.budget as Partial<BudgetConfig> | undefined) },
    customerLimits: { ...dg.customerLimits },
    escalation: { ...DEFAULT_FORM.escalation, ...s.escalation } as EscalationRules,
    productExclusions: { productIds: [...dg.productExclusions.productIds] },
  }
}

/**
 * Form state → the exact block Save writes. `handleSave` sends this and the dirty check compares
 * it against the stored block (`matchesStored`), so a default shown for a missing key, a value
 * normalised on display, the legacy MAR-311 migration and the stale-minimum delete below all make
 * the form dirty by construction — there is no second copy of the rule to drift.
 */
const buildGovernancePayload = (formData: FormData, stored: StoredDiscountGovernance | undefined) => {
  // Sanitize before it reaches the API: freeShippingMinOrder is a positive number or
  // absent — never an empty string / 0 placeholder on the workspace record.
  const { freeShippingMinOrder, ...rest } = formData
  const keepMinOrder =
    formData.freeShippingEnabled && typeof freeShippingMinOrder === 'number' && freeShippingMinOrder > 0
  // `toStoredGovernance` spreads the stored block first, so a key this form does not edit
  // survives the save, and writes the safeguards under the keys the enforcer reads.
  const payload = toStoredGovernance({ ...rest, ...(keepMinOrder ? { freeShippingMinOrder } : {}) }, stored)
  // The stored spread would otherwise carry a minimum the merchant just cleared.
  if (!keepMinOrder) delete payload.freeShippingMinOrder
  return payload
}

/**
 * One of the "Finer limits": a ruled row naming the group, with an Open button that reveals its
 * controls below the row (MAR-385, v6). The button is named by the group as well as its verb, so
 * five "Open"s are five different controls to a screen reader.
 */
const FinerLimit = ({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const bodyId = `dg-${useId().replace(/:/g, '')}`
  return (
    <>
      <SettingRow
        title={title}
        description={subtitle}
        aside={
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={isOpen}
            aria-controls={bodyId}
            aria-label={`${isOpen ? 'Close' : 'Open'} ${title}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? 'Close' : 'Open'}
          </Button>
        }
      />
      {isOpen && (
        <div id={bodyId}>
          <Card tone="recessed">
            <Stack>{children}</Stack>
          </Card>
        </div>
      )}
    </>
  )
}

const DiscountGovernanceSection = ({ onClose }: DiscountGovernanceSectionProps) => {
  const { currentWorkspace, isLoading, setCurrentWorkspace } = useWorkspaceStore()
  const [formData, setFormData] = useState<FormData>(() => formFromStored(currentWorkspace?.discountGovernance))
  // The product-ID field's raw text. Re-joining the parsed list on every keystroke ate the
  // comma the merchant had just typed ("333," → "333"), so two IDs could not be entered.
  const [productIdsText, setProductIdsText] = useState(() => formData.productExclusions.productIds.join(', '))
  const [isUpdating, setIsUpdating] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [budgetStatus, setBudgetStatus] = useState<{ spent: number; remaining: number; utilization: number } | null>(
    null,
  )
  /**
   * What she does above the ceiling — the SERVER's sentence (MAR-300), never a constant here.
   *
   * It depends on `AUTHORITY_ESCALATION_ENABLED`, which only the server knows; governance-api's
   * authority GET returns it as `aboveCeiling`, from the same Terraform value the gateway acts on.
   * A constant here said the dark behaviour whatever the flag. Absent or failed → nothing is
   * said, never a guess. Stamped with the workspace it was read for and scoped AT READ, so a
   * store switch cannot show the previous store's sentence for a frame.
   */
  const [ceilingSentence, setCeilingSentence] = useState<{ workspaceId: string; text: string } | null>(null)
  const aboveCeiling =
    ceilingSentence && ceilingSentence.workspaceId === currentWorkspace?.id ? ceilingSentence.text : null

  useEffect(() => {
    const workspaceId = currentWorkspace?.id
    // A sentence belongs only to the completed read that produced it. Clear before each read so
    // revisiting a workspace cannot revive its previous flag state while this request is pending
    // or after it fails.
    setCeilingSentence(null)
    if (!workspaceId) return
    let current = true
    governanceApi
      .getAuthority(workspaceId)
      .then((res) => {
        const text = res?.success ? res.data?.aboveCeiling : undefined
        if (!current) return
        setCeilingSentence(typeof text === 'string' && text ? { workspaceId, text } : null)
      })
      .catch(() => {
        // Nothing is said rather than a guess; the rest of the section does not depend on it.
        if (current) setCeilingSentence(null)
      })
    return () => {
      current = false
    }
  }, [currentWorkspace?.id])

  useEffect(() => {
    const next = formFromStored(currentWorkspace?.discountGovernance)
    setFormData(next)
    setProductIdsText(next.productExclusions.productIds.join(', '))
    // Re-hydrate on a workspace switch only — not on every store write, which would discard edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id])

  // Fetch real budget status from discount-governance Lambda
  useEffect(() => {
    const fetchBudgetStatus = async () => {
      if (!currentWorkspace?.id || !formData.enabled || formData.budget.amount <= 0) {
        setBudgetStatus(null)
        return
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const token = await (window as any).Clerk?.session?.getToken()
        const response = await fetch(
          `${API_BASE_URL}/workspaces/${currentWorkspace.id}/governance`,
          {
            // Only Authorization is in the route's Access-Control-Allow-Headers
            // (see api-gateway module governance OPTIONS integration). Adding
            // x-workspace-id triggers a CORS preflight failure, and the
            // discount-governance Lambda reads workspaceId from the URL path
            // anyway, so the header is redundant.
            headers: { Authorization: `Bearer ${token}` },
          },
        )

        if (response.ok) {
          const result = await response.json()
          if (result.success && result.data?.budget) {
            setBudgetStatus({
              spent: result.data.budget.spentAmount || 0,
              remaining: result.data.budget.remaining ?? formData.budget.amount,
              utilization: result.data.budget.utilizationPercent || 0,
            })
          } else {
            setBudgetStatus({ spent: 0, remaining: formData.budget.amount, utilization: 0 })
          }
        } else {
          setBudgetStatus({ spent: 0, remaining: formData.budget.amount, utilization: 0 })
        }
      } catch (error) {
        console.error('Failed to fetch budget status:', error)
        setBudgetStatus({ spent: 0, remaining: formData.budget.amount, utilization: 0 })
      }
    }

    fetchBudgetStatus()
  }, [currentWorkspace?.id, formData.enabled, formData.budget.amount])

  const handleSave = async () => {
    if (!currentWorkspace) return

    try {
      setIsUpdating(true)
      setMessage(null)

      const payload = buildGovernancePayload(formData, currentWorkspace.discountGovernance)

      const response = await workspacesApi.update(currentWorkspace.id, {
        discountGovernance: payload,
      } as never)

      if (!response.success) throw new Error(response.error || 'Failed to update settings')

      setCurrentWorkspace({
        ...currentWorkspace,
        discountGovernance: payload as unknown as StoredDiscountGovernance,
      })

      // The store now holds what was sent, which is what the dirty check compares against.
      setMessage('Settings saved successfully!')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Failed to save settings')
    } finally {
      setIsUpdating(false)
    }
  }

  const updateChannelLimit = (channel: keyof ChannelLimits, value: number) => {
    setFormData((prev) => ({ ...prev, channelLimits: { ...prev.channelLimits, [channel]: value } }))
  }

  const updateCustomerTypeLimit = (type: keyof CustomerTypeLimits, value: number) => {
    setFormData((prev) => ({ ...prev, customerTypeLimits: { ...prev.customerTypeLimits, [type]: value } }))
  }

  const channelLabels: Record<keyof ChannelLimits, string> = {
    chat: 'Chat',
    cart_recovery: 'Cart Recovery',
    post_browse: 'Post-Browse',
    inspiration: 'Inspiration',
    email_reply: 'Email Reply',
    sms: 'SMS',
  }

  const customerTypeLabels: Record<keyof CustomerTypeLimits, string> = {
    new: 'New Customer',
    returning: 'Returning Customer',
    vip: 'VIP Customer',
  }

  const isError = message != null && !message.includes('success')
  // D-27: dirty when what Save would write differs from what is stored (`storedValue.ts`).
  const stored = currentWorkspace?.discountGovernance
  const dirty = !matchesStored(buildGovernancePayload(formData, stored), stored)

  /** Cancel: back to what is stored for this workspace — the same reader the form seeds from. */
  const handleCancel = () => {
    const next = formFromStored(currentWorkspace?.discountGovernance)
    setFormData(next)
    setProductIdsText(next.productExclusions.productIds.join(', '))
    setMessage(null)
  }
  void onClose

  // The budget meter is always drawn (v6): with no amount it says so rather than vanishing, and
  // before the spend read lands it shows the amount against an unknown spend, never a zero.
  // "No budget" is read off the STORED block, not the form: with nothing stored the form shows
  // its $100 default, but discount-governance budgets against the stored amount (`|| 0`), so an
  // unsaved default refuses every discount as `budget_exceeded` — the meter must not show it.
  const budgetAmount = formData.budget.amount
  const storedBudgetAmount = (currentWorkspace?.discountGovernance as { budget?: { amount?: unknown } } | undefined)
    ?.budget?.amount
  const noBudget = !(typeof storedBudgetAmount === 'number' && storedBudgetAmount > 0) || budgetAmount <= 0
  const periodWord = formData.budget.period.charAt(0).toUpperCase() + formData.budget.period.slice(1)
  const dollars = (cents: number) => `$${(cents / 100).toFixed(0)}`
  const meter =
    noBudget
      ? { value: 0, text: 'No budget set', muted: true }
      : budgetStatus
        ? { value: Math.min(budgetStatus.utilization, 100), text: `${dollars(budgetStatus.spent)} / ${dollars(budgetAmount)}`, muted: false }
        : { value: 0, text: `— / ${dollars(budgetAmount)}`, muted: true }

  return (
    <Card>
      <CardHeader
        titleAs="h2"
        title="Discounts"
        description={`Below your ceiling Marva decides freely.${aboveCeiling ? ` ${aboveCeiling}` : ''}`}
        aside={
          <Toggle
            checked={formData.enabled}
            label="Enable discount governance"
            labelHidden
            onChange={() => setFormData((prev) => ({ ...prev, enabled: !prev.enabled }))}
          />
        }
      />

      {message && (
        <Alert tone={isError ? 'danger' : 'success'} live={isError ? 'assertive' : 'polite'}>
          {message}
        </Alert>
      )}

      {!formData.enabled && (
        <Hint>Off: Marva offers no discount of her own. Switch it on to let her offer one within these limits.</Hint>
      )}

      {formData.enabled && (
        <>
          <FieldGrid>
            <Slider
              label="Global max discount"
              hint="The ceiling on any single code Marva authorises."
              value={formData.maxDiscountPercent}
              min={5}
              max={50}
              unit="%"
              onChange={(v) => setFormData((prev) => ({ ...prev, maxDiscountPercent: v }))}
            />
            <Stack gap="sm">
              <Meter label={`Budget · ${periodWord.toLowerCase()}`} value={meter.value} valueText={meter.text} muted={meter.muted} />
              <Hint>
                {noBudget
                  ? 'No discounts until a budget is saved. Set an amount under Budget controls, below.'
                  : budgetStatus
                    ? `${budgetStatus.utilization}% utilised · ${dollars(budgetStatus.remaining)} remaining`
                    : `${periodWord} budget of ${dollars(budgetAmount)}.`}
              </Hint>
            </Stack>
          </FieldGrid>

          <FieldGrid>
            <Field
              label="Minimum cart value ($)"
              type="number"
              prefix="$"
              min={0}
              step={10}
              value={formData.minCartValue}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  minCartValue: parseFloat((e.target as HTMLInputElement).value) || 0,
                }))
              }
            />
            <Field
              as="select"
              label="Code expiry"
              value={formData.codeExpiryHours}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  codeExpiryHours: parseInt((e.target as HTMLSelectElement).value, 10),
                }))
              }
            >
              <option value="24">24 hours</option>
              <option value="48">48 hours</option>
              <option value="72">72 hours</option>
              <option value="168">7 days</option>
            </Field>
          </FieldGrid>
          <Toggle
            checked={formData.createInShopify}
            label="Create codes in Shopify"
            description="Generate real, single-use discount codes in your store rather than placeholders."
            onChange={() => setFormData((prev) => ({ ...prev, createInShopify: !prev.createInShopify }))}
          />
          <Toggle
            checked={formData.freeShippingEnabled}
            label="Free shipping offers"
            description="Free shipping instead of a discount — often the cheaper lever. Only if you charge for shipping: she can't read your shipping zones yet, so a store that ships free would see redundant offers."
            onChange={() => setFormData((prev) => ({ ...prev, freeShippingEnabled: !prev.freeShippingEnabled }))}
          />
          {formData.freeShippingEnabled && (
            <Field
              label="Minimum order for free shipping"
              hint="Optional. Leave empty for no minimum."
              type="number"
              min={0}
              value={formData.freeShippingMinOrder}
              placeholder="e.g. 40"
              onChange={(e) => {
                const raw = (e.target as HTMLInputElement).value
                if (raw === '') {
                  setFormData((prev) => ({ ...prev, freeShippingMinOrder: '' }))
                  return
                }
                const n = Number(raw)
                // Validated here (not just at the API): only a finite, non-negative number
                if (Number.isFinite(n) && n >= 0) {
                  setFormData((prev) => ({ ...prev, freeShippingMinOrder: n }))
                }
              }}
            />
          )}

          <Eyebrow tone="muted">Finer limits</Eyebrow>
          <div>
            <FinerLimit
              title="Channel limits"
              subtitle="Per-channel ceilings for Chat, Cart Recovery, Post-Browse, Inspiration, Email Reply and SMS. The lower of this and the global max applies."
            >
              <TileGrid>
                {(Object.keys(channelLabels) as Array<keyof ChannelLimits>).map((channel) => (
                  <Slider
                    key={channel}
                    label={channelLabels[channel]}
                    value={formData.channelLimits[channel]}
                    min={5}
                    max={50}
                    unit="%"
                    onChange={(v) => updateChannelLimit(channel, v)}
                  />
                ))}
              </TileGrid>
            </FinerLimit>

            <FinerLimit
              title="Customer type limits"
              subtitle="New, Returning and VIP. VIP status is determined by Shopify tags or purchase history."
            >
              <TileGrid>
                {(Object.keys(customerTypeLabels) as Array<keyof CustomerTypeLimits>).map((type) => (
                  <Slider
                    key={type}
                    label={customerTypeLabels[type]}
                    value={formData.customerTypeLimits[type]}
                    min={5}
                    max={50}
                    unit="%"
                    onChange={(v) => updateCustomerTypeLimit(type, v)}
                  />
                ))}
              </TileGrid>
              <Alert tone="info">
                <b>VIP detection:</b> customers with a "VIP" tag in Shopify, or 5+ orders, or $500+ lifetime spend.
              </Alert>
            </FinerLimit>

            <FinerLimit title="Budget controls" subtitle="Cap total discount spend and per-customer frequency.">
              <TileGrid>
                <Field
                  as="select"
                  label="Budget period"
                  value={formData.budget.period}
                  onChange={(e) => {
                    const period = (e.target as HTMLSelectElement).value as BudgetConfig['period']
                    setFormData((prev) => ({ ...prev, budget: { ...prev.budget, period } }))
                  }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </Field>
                <Field
                  label="Budget amount ($)"
                  type="number"
                  prefix="$"
                  min={0}
                  step={100}
                  value={formData.budget.amount / 100}
                  onChange={(e) => {
                    const amount = (parseFloat((e.target as HTMLInputElement).value) || 0) * 100
                    setFormData((prev) => ({ ...prev, budget: { ...prev.budget, amount } }))
                  }}
                />
                {/* MAR-311: what these two write is a known backend bug, tracked there — this
                    migration changes only how they are drawn, never the value or its key. */}
                <Field
                  label="Per-customer limit"
                  type="number"
                  min={1}
                  hint="codes per customer per period"
                  value={formData.customerLimits.maxPerCustomer}
                  onChange={(e) => {
                    const maxPerCustomer = Math.max(1, parseInt((e.target as HTMLInputElement).value, 10) || 1)
                    setFormData((prev) => ({ ...prev, customerLimits: { ...prev.customerLimits, maxPerCustomer } }))
                  }}
                />
                <Field
                  as="select"
                  label="Per-customer period"
                  value={formData.customerLimits.period}
                  onChange={(e) => {
                    const period = (e.target as HTMLSelectElement).value as PerCustomerPeriod
                    setFormData((prev) => ({ ...prev, customerLimits: { ...prev.customerLimits, period } }))
                  }}
                >
                  {PER_CUSTOMER_PERIODS.map((period) => (
                    <option key={period} value={period}>
                      {PER_CUSTOMER_PERIOD_LABELS[period]}
                    </option>
                  ))}
                </Field>
              </TileGrid>
              <Alert tone="info">
                <b>Circuit breaker:</b> if the daily budget is exhausted before noon, discounts are blocked for the rest
                of the day to prevent anomalies.
              </Alert>
            </FinerLimit>

            <FinerLimit
              title="Escalation rules"
              subtitle="Flag high-value requests for review. Escalated requests are still processed, but marked for attention."
            >
              <TileGrid>
                <Field
                  label="Escalate if discount exceeds"
                  type="number"
                  suffix="%"
                  min={10}
                  max={50}
                  value={formData.escalation.thresholdPercent}
                  onChange={(e) => {
                    const thresholdPercent = parseInt((e.target as HTMLInputElement).value, 10) || 25
                    setFormData((prev) => ({ ...prev, escalation: { ...prev.escalation, thresholdPercent } }))
                  }}
                />
                <Field
                  label="Escalate if cart value exceeds"
                  type="number"
                  prefix="$"
                  min={100}
                  step={100}
                  value={formData.escalation.thresholdCartValue}
                  onChange={(e) => {
                    const thresholdCartValue = parseFloat((e.target as HTMLInputElement).value) || 500
                    setFormData((prev) => ({ ...prev, escalation: { ...prev.escalation, thresholdCartValue } }))
                  }}
                />
              </TileGrid>
            </FinerLimit>

            <FinerLimit title="Product exclusions" subtitle="Exclude specific products from discounts.">
              <Field
                id="dg-excluded-product-ids"
                label="Excluded product IDs"
                value={productIdsText}
                placeholder="e.g. 7981234567890, 7981234567891"
                hint="Shopify product IDs, separated by commas or spaces — the number at the end of the product's admin URL, or its gid://shopify/Product/… ID."
                onChange={(e) => {
                  const text = (e.target as HTMLInputElement).value
                  setProductIdsText(text)
                  setFormData((prev) => ({
                    ...prev,
                    productExclusions: {
                      productIds: text.split(/[\s,]+/).filter(Boolean),
                    },
                  }))
                }}
              />
            </FinerLimit>
          </div>
        </>
      )}

      <SaveBar
        placement="footer"
        state={isUpdating ? 'saving' : dirty && !isLoading ? 'dirty' : 'clean'}
        note="Changes apply from her next message."
        saveLabel="Save settings"
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </Card>
  )
}

export default DiscountGovernanceSection
